import * as React from 'react';

import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';

import { useCan } from '@/components/can/Can';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useDropdown } from '@/hooks/useDropdown';
import { useDynamicLocale } from '@/hoslog-intl';
import { splitLocalizedName } from '@/lib/splitHostName';
import { additionalValue, Pagination } from '@/react-query/context/ApiClientContextController';
import { GalleryManagement } from '@/services/galleryManagement/galleryManagement.types';
import { useParams } from '@tanstack/react-router';
import { AddGalleryImageForm } from '../../../form/AddGalleryImageForm';
import { useDeleteHostGallery } from '../../../hooks/useDeleteHostGallery';
import { StoreGalleryManagementTableActionBar } from './ActionBar';
import { getColumns } from './Column';

export function StoreGalleryManagementTable({
  data,
  isLoading,
  pagination,
  additionalValue,
}: {
  data: GalleryManagement[];
  isLoading: boolean;
  pagination: Pagination;
  additionalValue: additionalValue;
}) {
  const { openConfirmation } = useConfirmationStore();
  const { t, locale } = useDynamicLocale();
  const { mutateAsync } = useDeleteHostGallery();

  const { onOpen } = useDialogFormStore();
  const canAddGallery = useCan({ allOf: [PERMS.gallery.premium.create] });
  const columns = React.useMemo(
    () => getColumns(mutateAsync, openConfirmation, onOpen, t),
    [mutateAsync, openConfirmation, onOpen, t],
  );
  const params = useParams({
    from: '/(app)/store-information/premium-stores/gallery/$store-gallery/',
  });
  const clubId = params['store-gallery'] || '';
  const { data: options } = useDropdown(
    'clubName',
    additionalValue?.commonClubId,
    undefined,
    undefined,
    Boolean(additionalValue?.commonClubId),
  );
  const { japanese, english } = splitLocalizedName(options?.[0]?.label);
  const clubName = locale === 'en' ? english : japanese;
  const onAddNew = () => {
    onOpen({
      modalTitle: 'storeManagement.galleryManagement.page.addGallery.manageClubImage',
      title: 'Manage HostNameGallery',
      okText: t('common.page.button.submit'),
      size: 'lg',
      component: <AddGalleryImageForm clubId={clubId} hostId="" type="store" />,
    });
  };

  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const { table } = useDataTable({
    data,
    columns,
    pageCount: pageCount,
    initialState: {
      sorting: [{ id: 'id', desc: true }],
      columnPinning: { right: ['actions'] },
    },
    meta: {
      title: `${clubName} ${t('storeManagement.galleryManagement.page.table.title')}`,
      action: onAddNew,
      actionKey: canAddGallery ? 'onAddNew' : '',
      loading: isLoading,
      totalPage: pagination.totalItems,
    },
    getRowId: (originalRow) => String(originalRow.id),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable table={table}>
        <DataTableToolbar />
        <StoreGalleryManagementTableActionBar />
      </DataTable>
    </>
  );
}
